import { useReadingByMonth } from '@/features/library/hooks/useReadingByMonth.js';

const MONTH_FORMAT = new Intl.DateTimeFormat('fr-FR', { month: 'short' });

/**
 * Bar chart livres lus par mois (12 derniers mois).
 * Source : endpoint /profile/reading-by-month qui agrège les ShelfEntry done.
 */
export function BarChart(): JSX.Element {
  const { data, isLoading } = useReadingByMonth();
  const items = data?.items ?? [];

  const values = items.map((i) => i.count);
  const max = Math.max(...values, 1);
  const total = values.reduce((s, v) => s + v, 0);
  const avg = items.length > 0 ? (total / items.length).toFixed(1) : '0';
  const currentMonthKey = monthKey(new Date());

  return (
    <section className="rounded-3xl bg-paper p-6 shadow-md">
      <h3 className="font-serif text-base font-semibold text-brown">
        📊 Lectures des 12 derniers mois
      </h3>
      <p className="font-script text-sm text-brown-soft">
        moyenne {avg} livre{Number(avg) > 1 ? 's' : ''} / mois
      </p>

      {isLoading && (
        <p className="mt-3 text-sm text-brown-soft">Chargement…</p>
      )}

      {!isLoading && items.length > 0 && (
        <>
          <div className="bar-chart mt-4">
            {items.map((item) => {
              const heightPct = (item.count / max) * 100;
              const label = formatMonthLabel(item.month);
              return (
                <div
                  key={item.month}
                  className={`bar${item.month === currentMonthKey ? ' current' : ''}`}
                  style={{ height: `${Math.max(4, heightPct)}%` }}
                  title={`${label} : ${item.count} livre${item.count > 1 ? 's' : ''}`}
                />
              );
            })}
          </div>
          <div className="bar-labels">
            {items.map((item) => (
              <span key={item.month} className="bar-label">
                {formatMonthLabel(item.month).charAt(0)}
              </span>
            ))}
          </div>
        </>
      )}
    </section>
  );
}

function formatMonthLabel(monthKeyStr: string): string {
  const parts = monthKeyStr.split('-');
  const yearStr = parts[0];
  const monthStr = parts[1];
  if (!yearStr || !monthStr) return '—';
  const date = new Date(Number(yearStr), Number(monthStr) - 1, 1);
  return MONTH_FORMAT.format(date);
}

function monthKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}
